// TxSimpleModal — modal simples compartilhado.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.12
// Pedido por 4 LOTES (L11 X1, L12, L18, L23 C5).
//
// Os dois defeitos foram conferidos neste arquivo antes de mexer:
//   1. O `DialogTitle` cravava `background:'#f8f8f8'` e `borderBottom:'1px solid #f0f0f0'`.
//      Hex literal não vira nada no esquema escuro: a faixa continuava cinza-claro sobre o
//      `Paper` escuro. O canvas não tem faixa nenhuma no cabeçalho de modal — o artboard
//      escreve `.dlg{background:var(--bg-paper);border-radius:var(--radius-card);
//      box-shadow:var(--shadow-dialog);padding:22px 28px 14px}` e o `.dlg-head` é só
//      `display:flex;align-items:center;justify-content:space-between;gap:12px;
//      margin-bottom:14px`, sem borda e sem fundo próprio. Agora o cabeçalho é
//      `bgcolor:'background.paper'` e a linha some; a separação que resta é a do
//      `DialogContent dividers`, que já sai por token (`divider`) e não muda de lugar.
//   2. O X era `<Iconify icon="solar:close-circle-bold" width={30}>` — ícone CHEIO, dentro
//      de um `IconButton` redondo de ~38px. Contra §3.4 (o canvas é traço: `fill:none;
//      stroke:currentColor`) e contra §6.2 (não existe botão-ícone redondo no canvas).
//      Agora é `DsIconButton tone="bare" icon="fechar"`, que é literalmente a regra
//      `.iconbtn{width:30px;height:30px;display:grid;place-items:center;border-radius:8px;
//      color:var(--action-active)}` medida nos artboards de modal.
//
// O `aria-label="Fechar"` e o `onClick={solicitarFechamento}` são os mesmos: o X continua
// passando pela guarda de saída, que é justamente o que L18 registrou perder quando a view
// desenhava o próprio X com `title={null}`. Nada da API do componente mudou.

import type { ReactNode } from 'react';
import type { DialogProps } from '@mui/material';

import { useState, useCallback } from 'react';

import { Box, Dialog, DialogTitle, DialogContent } from '@mui/material';

import { DsIconButton } from 'src/auditoria/components/ds';

import { TxModalConfirmarDescarte } from './tx-modal-confirmar-descarte';
import { useGuardaDeSaida, TxModalGuardaContext } from './tx-modal-guarda-saida';

type TxSimpleModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  content?: ReactNode;
  dialogProps?: Partial<DialogProps>;
  maxWidth?: DialogProps['maxWidth'];
};

export function TxSimpleModal({
  open,
  onClose,
  title = '',
  content,
  maxWidth = 'sm', // <- TAMANHO PADRÃO
  dialogProps = {},
}: TxSimpleModalProps) {
  // Guarda de saída: ver `tx-modal-guarda-saida.tsx`. Sem campo preenchido ela
  // responde `false` e nada muda em relação ao comportamento antigo.
  const { conteudoRef, estaSujo, contexto } = useGuardaDeSaida();
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  // Saída iniciada pelo usuário (ESC, clique fora, X). Só o que tem trabalho a
  // perder passa pela confirmação; o resto fecha na hora, como sempre fechou.
  const solicitarFechamento = useCallback(() => {
    if (estaSujo()) {
      setConfirmandoDescarte(true);
      return;
    }
    onClose();
  }, [estaSujo, onClose]);

  const aoFecharDialog = useCallback<NonNullable<DialogProps['onClose']>>(
    (_evento, motivo) => {
      if (motivo === 'backdropClick' || motivo === 'escapeKeyDown') {
        solicitarFechamento();
        return;
      }
      onClose();
    },
    [solicitarFechamento, onClose]
  );

  const descartar = useCallback(() => {
    setConfirmandoDescarte(false);
    onClose();
  }, [onClose]);

  const continuarPreenchendo = useCallback(() => setConfirmandoDescarte(false), []);

  // O fechamento programático (`fecharModalAtual`, usado depois de salvar) não
  // passa por aqui: continua fechando direto, sem perguntar nada.
  const confirmacao = (
    <TxModalConfirmarDescarte
      aberto={confirmandoDescarte}
      aoContinuar={continuarPreenchendo}
      aoDescartar={descartar}
    />
  );

  // Se title for null, renderiza sem cabeçalho e sem padding padrão
  if (title === null) {
    return (
      <TxModalGuardaContext.Provider value={contexto}>
        <Dialog fullWidth open={open} onClose={aoFecharDialog} maxWidth={maxWidth} {...dialogProps}>
          <DialogContent ref={conteudoRef} sx={{ p: 0 }}>
            {content}
          </DialogContent>
        </Dialog>
        {confirmacao}
      </TxModalGuardaContext.Provider>
    );
  }

  return (
    <TxModalGuardaContext.Provider value={contexto}>
      <Dialog fullWidth open={open} onClose={aoFecharDialog} maxWidth={maxWidth} {...dialogProps}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            pt: 1.5,
            pb: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              '& svg': {
                verticalAlign: 'middle',
                mr: 2,
                width: 30,
                height: 30,
                color: 'success.main',
              },
            }}
          >
            {title}
          </Box>

          <DsIconButton
            tone="bare"
            icon="fechar"
            aria-label="Fechar"
            onClick={solicitarFechamento}
          />
        </DialogTitle>

        <DialogContent ref={conteudoRef} dividers sx={{ pt: 3, pb: 3, px: 3 }}>
          {content}
        </DialogContent>
      </Dialog>
      {confirmacao}
    </TxModalGuardaContext.Provider>
  );
}
